import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ShieldPlus, UserPlus, Trash2, RefreshCw, Star } from 'lucide-react';

const SUPER_ADMIN_EMAIL = 'jason@westlandfmc.com';

/**
 * AdminAdministratorsPanel
 * Lists current admins and (for the super admin only) lets them grant/revoke
 * the `admin` role by email. Non-super admins see the roster but not the
 * add/remove controls.
 */
const AdminAdministratorsPanel = () => {
  const { user, API } = useContext(AuthContext);
  const isSuperAdmin = (user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL;

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/administrators`);
      setAdmins(res.data.administrators || []);
    } catch (e) {
      toast.error('Failed to load administrators');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const setAdminRole = async (email, enabled) => {
    const res = await axios.post(`${API}/admin/users/by-email/admin`, { email, enabled });
    return res.data;
  };

  const handleAdd = async () => {
    const email = newEmail.trim();
    if (!email) {
      toast.error('Enter an email');
      return;
    }
    setAdding(true);
    try {
      const data = await setAdminRole(email, true);
      toast.success(`${data.user?.name || email} is now an admin`);
      setNewEmail('');
      fetchAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (adminUser) => {
    if (!window.confirm(`Remove admin access for ${adminUser.email}?`)) return;
    setRemovingEmail(adminUser.email);
    try {
      await setAdminRole(adminUser.email, false);
      toast.success(`Removed admin access from ${adminUser.email}`);
      fetchAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
    } finally {
      setRemovingEmail(null);
    }
  };

  return (
    <Card data-testid="administrators-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-[#2B6B7F]">
              <ShieldPlus className="w-5 h-5" />
              Manage Administrators
            </CardTitle>
            <CardDescription>
              {isSuperAdmin
                ? 'Grant full admin access to another member. Only you (the primary admin) can add or remove admins.'
                : 'Current administrators. Only the primary admin can add or remove admins.'}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAdmins}
            data-testid="administrators-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add form — super admin only */}
        {isSuperAdmin && (
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              placeholder="user@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              disabled={adding}
              className="flex-1"
              data-testid="admin-email-input"
            />
            <Button
              onClick={handleAdd}
              disabled={adding || !newEmail.trim()}
              className="bg-[#2B6B7F] hover:bg-[#245968] text-white"
              data-testid="admin-add-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {adding ? 'Adding…' : 'Grant Admin'}
            </Button>
          </div>
        )}

        {/* Roster */}
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : admins.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No administrators yet.</p>
        ) : (
          <div className="space-y-2" data-testid="administrators-list">
            {admins.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50"
                data-testid={`administrator-row-${a.email}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-800 truncate">{a.name || a.email}</span>
                    {a.is_super_admin && (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
                        <Star className="w-3 h-3 mr-1" />
                        Primary Admin
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{a.email}</p>
                </div>
                {isSuperAdmin && !a.is_super_admin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemove(a)}
                    disabled={removingEmail === a.email}
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    data-testid={`administrator-remove-${a.email}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    {removingEmail === a.email ? 'Removing…' : 'Remove'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminAdministratorsPanel;
